import { Toaster as Sonner, toast } from "sonner";
import { useMemo } from "react";
import { usePublicTheme } from "@/context/PublicThemeContext";
import { cn } from "@/lib/utils";

const MONO = "'JetBrains Mono', ui-monospace, monospace";

/** Public sayfalar için; yalnızca PublicThemeProvider içinde kullanın. */
export function PublicToaster({ toastOptions: userToastOptions, ...props }) {
  const { theme } = usePublicTheme();
  const isLight = theme === "light";

  const toastOptions = useMemo(() => {
    const u = userToastOptions && typeof userToastOptions === "object" ? userToastOptions : {};
    const uc = u.classNames && typeof u.classNames === "object" ? u.classNames : {};
    const uStyle =
      typeof u.style === "object" && u.style !== null && !Array.isArray(u.style) ? u.style : {};

    return {
      ...u,
      style: {
        fontFamily: uStyle.fontFamily ?? MONO,
        ...uStyle,
      },
      classNames: {
        toast: cn(
          "group toaster-pub !rounded-none border-2 border-dashed !p-4",
          "font-mono text-xs uppercase tracking-[0.08em]",
          "transition-all duration-200 ease-out hover:brightness-[1.03]",
          "hover:-translate-y-px hover:shadow-xl",
          isLight
            ? [
                "!shadow-lg",
                "!bg-gradient-to-br !from-[#fefefe] !via-white !to-neutral-50/98",
                "!border-neutral-900/35 !text-neutral-900",
                "hover:!border-[#FF4F00]/45",
                "hover:!bg-gradient-to-br hover:!from-white hover:!via-[#fffbf8] hover:!to-orange-50/40",
              ]
            : [
                "!shadow-xl",
                "!bg-gradient-to-br !from-[#0C0C0F] !via-[#0e0e11] !to-[#131318]",
                "!border-white/22 !text-[#FAFAFA]",
                "hover:!border-white/38",
              ],
          uc.toast,
        ),
        title: cn(
          isLight
            ? "!font-bold !text-xl sm:!text-2xl !leading-tight !normal-case !tracking-tight !text-neutral-950"
            : "!text-[#FAFAFA] uppercase",
          uc.title,
        ),
        description: cn(
          isLight
            ? "!font-bold !text-lg sm:!text-xl !leading-snug !normal-case !tracking-tight !mt-1.5 font-mono !text-neutral-800"
            : "!text-[#b9b3a8] !text-xs !font-normal !normal-case !mt-1.5 opacity-95",
          uc.description,
        ),
        actionButton: cn(
          "rounded-none !bg-[#FF4F00] !text-neutral-950 !font-semibold uppercase tracking-[0.06em] hover:!bg-[#e64700]",
          uc.actionButton,
        ),
        cancelButton: cn(
          "rounded-none border border-dashed !bg-transparent uppercase tracking-[0.06em]",
          isLight ? "!border-neutral-400 hover:!bg-neutral-100" : "!border-white/25 hover:!bg-white/5",
          uc.cancelButton,
        ),
        closeButton: cn(
          "rounded-none",
          isLight ? "!border-neutral-400 hover:!bg-neutral-200/40" : "!border-white/25 hover:!bg-white/10",
          uc.closeButton,
        ),
      },
    };
  }, [isLight, userToastOptions]);

  return (
    <Sonner
      theme={theme}
      {...props}
      className={cn("group toaster-public pointer-events-auto", props.className)}
      toastOptions={toastOptions}
    />
  );
}

/**
 * Admin rotalarında sabit koyu toastr — PublicThemeProvider dışında da çalışır.
 */
export function AdminToaster({ toastOptions: userToastOptions, ...props }) {
  const toastOptions = useMemo(() => {
    const u = userToastOptions && typeof userToastOptions === "object" ? userToastOptions : {};
    const uc = u.classNames && typeof u.classNames === "object" ? u.classNames : {};
    const uStyle =
      typeof u.style === "object" && u.style !== null && !Array.isArray(u.style) ? u.style : {};

    return {
      ...u,
      style: {
        background: uStyle.background ?? "#0C0C0F",
        color: uStyle.color ?? "#FAFAFA",
        borderRadius: uStyle.borderRadius ?? "0px",
        fontFamily: uStyle.fontFamily ?? MONO,
        fontSize: uStyle.fontSize ?? "12px",
        textTransform: uStyle.textTransform ?? "uppercase",
        letterSpacing: uStyle.letterSpacing ?? "0.08em",
        ...uStyle,
      },
      classNames: {
        toast: cn(
          "group toaster-admin border-2 border-dashed border-white/[0.16] rounded-none shadow-xl",
          "bg-gradient-to-br from-[#0C0C0F] via-[#0f0f13] to-[#141418]",
          "hover:border-white/[0.28] hover:brightness-[1.04] transition-all duration-200 !p-4",
          uc.toast,
        ),
        title: cn("!text-[#FAFAFA]", uc.title),
        description: cn("!text-[#b9b3a8] !uppercase !text-xs !mt-1", uc.description),
        actionButton: uc.actionButton,
        cancelButton: uc.cancelButton,
        closeButton: uc.closeButton,
      },
    };
  }, [userToastOptions]);

  return (
    <Sonner
      {...props}
      theme="dark"
      className={cn("pointer-events-auto", props.className)}
      toastOptions={toastOptions}
    />
  );
}

export { toast };
